<script setup>
/**
 * FormControlModulecomponentsTitleColumn
 * Dynamic select dropdown filtering structural fields of type 'text'.
 * Automatically isolates and alphabetizes text columns, and hides the entire
 * form field via a side-effect watcher if no valid text columns are found.
 */
import { computed, watch, onMounted, getCurrentInstance } from 'vue';

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: [String, Number, Array],
        default: null
    }
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- Computed ---

/**
 * References global application map indexing valid primary table ids.
 */
const componentIds = computed(() => store.state.components.componentIds);

/**
 * Parses out component metadata fields matching a specific database table name,
 * isolates text attributes, and handles alphabetical sorting.
 */
const options = computed(() => {
    const tableName = props.currentData?.tableName;
    const currentId = props.currentData?.id;

    if (!currentId || !tableName) {
        return [];
    }

    const componentId = componentIds.value[tableName];
    if (!componentId) {
        return [];
    }

    // Retrieve fields array and filter for text configurations exclusively
    const structureFields = store.getters['componentStructure/get'](componentId) || [];
    const textFields = structureFields.filter(obj => obj.type === 'text');

    if (!textFields.length) {
        return [];
    }

    // Alphabetize text elements by field title
    const sortedFields = textFields.sort((a, b) => (a.title > b.title ? 1 : -1));

    // Formulate final drop-down option payload
    const dropdownOptions = [{ value: '', text: 'Select…' }];
    sortedFields.forEach((obj) => {
        dropdownOptions.push({
            value: obj.columnName,
            text: obj.title
        });
    });

    return dropdownOptions;
});

// --- Watchers & Lifecycle ---

/**
 * Monitors the options dataset. If it drops to empty, it triggers the side effect
 * to remove this selector from the active form layout scheme.
 */
watch(options, (newOptions) => {
    if (!newOptions || newOptions.length === 0) {
        if (props.field) {
            // TODO - Update with form-text feature once implemented.
            // It should state that no image columns exist.
            props.field.hidden = true;
        }
    }
}, { immediate: true });

onMounted(() => {
    /**
     * Set a default initialization value within the store tracking module
     * immediately upon component mounting.
     */
    store.dispatch(`${props.formId}/setDefaultValue`, {
        name: props.field.name,
        value: ''
    });
});
</script>

<template>
    <select
        :id="field.id"
        v-model="currentData[field.name]"
        :name="field.name"
        :required="field.required"
        class="form-select"
    >
        <option v-for="option in options" :key="option.value" :value="option.value">
            {{ option.text }}
        </option>
    </select>
</template>